import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { InputSwitch } from "primereact/inputswitch";
import { Toast } from "primereact/toast";
import agent from "../../agent";
import { SLQ_POSTAL_SERVICE_LOAD, SLQ_POSTAL_SERVICE_UNLOAD } from "../../constants/actionTypes";

function SLQPostalService() {
  const dispatch = useDispatch();
  const toast = useRef(null);
  const slqPostalServiceState = useSelector((state) => state.slqPostalService);
  const { slqPostalServices } = slqPostalServiceState || {};

  const [rows, setRows] = useState(10);
  const [first, setFirst] = useState(0);
  const [showDialog, setShowDialog] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [flags, setFlags] = useState({});

  useEffect(() => {
    const load = async () => {
      const pageIndex = Math.floor(first / rows);
      const list = await agent.SQLPostalService.getPostalServices(pageIndex, rows);
      dispatch({ type: SLQ_POSTAL_SERVICE_LOAD, payload: list });
    };
    load();
    return () => dispatch({ type: SLQ_POSTAL_SERVICE_UNLOAD });
  }, [dispatch, first, rows]);

  const onRowView = async (rowData) => {
    setShowDialog(true);
    setDetail(null);
    setFlags({});
    setDetailLoading(true);
    try {
      const data = await agent.SQLPostalService.getPostalService(rowData?.id);
      setDetail(data);
      // Try to find flags object; fallback to booleans at root
      const flagsObj = data?.flags || {};
      const booleanRoots = Object.fromEntries(Object.entries(data || {}).filter(([k, v]) => typeof v === 'boolean'));
      setFlags({ ...booleanRoots, ...flagsObj });
    } catch (e) {
      toast.current?.show({ severity: 'error', summary: 'Ошибка', detail: 'Не удалось загрузить детали', life: 4000 });
    } finally {
      setDetailLoading(false);
    }
  };

  const onHideDialog = () => {
    setShowDialog(false);
    setDetail(null);
    setFlags({});
  };

  const onSaveFlags = async () => {
    if (!detail?.id) return;
    try {
      const newData = {ptcActive: flags.ptcActive, isActive: flags.active};
      await agent.SQLPostalService.flagsPostalService(detail.id, newData);
      toast.current?.show({ severity: 'success', summary: 'Сохранено', detail: 'Флаги обновлены', life: 3000 });
      setShowDialog(false);
      setDetail(null);
      setFlags({});
      const pageIndex = Math.floor(first / rows);
      const list = await agent.SQLPostalService.getPostalServices(pageIndex, rows);
      dispatch({ type: SLQ_POSTAL_SERVICE_LOAD, payload: list });
    } catch (e) {
      toast.current?.show({ severity: 'error', summary: 'Ошибка', detail: 'Не удалось сохранить флаги', life: 4000 });
    }
  };

  const rowActionsTemplate = (rowData) => (
    <Button label="Детали" className="p-button-text" icon="pi pi-search" onClick={() => onRowView(rowData)} />
  );

  const onPage = (e) => {
    setFirst(e.first);
    setRows(e.rows);
  };

  const FlagRow = ({ name, value }) => (
        <div className="p-d-flex p-ai-center p-jc-between p-mb-2">
          <span>{name === 'active' ? 'Активный' : 'ЧГП Активный'
          }</span>
          <InputSwitch checked={!!value} onChange={(e) => setFlags({ ...flags, [name]: e.value })} />
        </div>
  );

  const flagsContent = () => {
    const entries = Object.entries(flags || {});
    if (!entries.length) return <div>Нет флагов для отображения</div>;
    return (
      <div>
        {entries.map(([k, v]) => (
          <FlagRow key={k} name={k} value={v} />
        ))}
      </div>
    );
  };

  const dialogFooter = (
    <div>
      <Button label="Отмена" className="p-button-text p-mr-2" onClick={onHideDialog} />
      <Button label="Сохранить" icon="pi pi-save" onClick={onSaveFlags} />
    </div>
  );

  return (
    <div className="datatable-doc-demo">
      <Toast ref={toast} />
      <div className="card">
        <h2>Поставщики SLQ</h2>
        <DataTable
          value={(slqPostalServices?.content) || []}
          lazy
          totalRecords={slqPostalServices?.totalElements || 0}
          paginator
          rows={rows}
          first={(slqPostalServices?.number || 0) * (slqPostalServices?.size || rows)}
          onPage={(e) => {
            setRows(e.rows);
            setFirst(e.first);
          }}
          rowsPerPageOptions={[10, 25, 50]}
          dataKey="id"
          rowHover
          emptyMessage="Данные не найдены"
          currentPageReportTemplate="Показано с {first} по {last} из {totalRecords} записей"
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
        >
          <Column headerStyle={{ width: '200px' }} columnKey="id" field="id" header="ID" sortable />
          <Column headerStyle={{ width: '300px' }} columnKey="nameRu" field="nameRu" header="Наименование" sortable />
          <Column headerStyle={{ width: '200px' }} columnKey="code" field="code" header="Код услуги" sortable />
          <Column headerStyle={{ width: '200px' }} columnKey="active" field="active" header="Активный" sortable body={(row) => (row.active ? 'Да' : 'Нет')} />
          <Column headerStyle={{ width: '200px' }} columnKey="ptcActive" field="ptcActive" header="ЧГП Активный" sortable body={(row) => (row.ptcActive ? 'Да' : 'Нет')} />
          <Column body={rowActionsTemplate} headerStyle={{ width: '150px' }} />
        </DataTable>
      </div>

      <Dialog
        header={`Детали поставщика ${detail?.nameRu || ''}`}
        visible={showDialog}
        style={{ width: '40rem' }}
        modal
        onHide={onHideDialog}
        footer={dialogFooter}
      >
        {detailLoading ? (
          <div>Загрузка...</div>
        ) : (
          <div>
            <div className="p-mb-3">
              <strong>ID:</strong> {detail?.id}
            </div>
            <div className="p-mb-3">
              <strong>Код услуги:</strong> {detail?.code}
            </div>
            <h4 className="p-mt-3">Флаги</h4>
            {flagsContent()}
          </div>
        )}
      </Dialog>
    </div>
  );
}

export default SLQPostalService;